import { z } from "zod";
import { Id } from "./common.ts";
import { CommenterMark, CommentersQuery, FlagKey, Job } from "./domain.ts";
import type { RouteDef } from "./routes.ts";

/**
 * People bulk moderation (`/people`, person page, person + comment quick panels): flag, mark, Block in Malfak, hide /
 * unhide / archive / delete all their comments, export. Every action is ONE job (jobs center, notifications, Retry, typed
 * failure reasons), audited per job and per person, RBAC (editor+; delete-all = admin/owner) and the `bulk_actions`
 * feature (more than one person, or any action over all of a person's comments).
 *
 * Instagram has no API to block or restrict a user: "Block in Malfak" is Malfak-side (see `ContactBlock` in domain.ts);
 * the real block is a deep link to the person's Instagram profile. Deleting uses `DELETE /{comment-id}` (only comments on
 * the account's own media); rows stay in Malfak with `deletion_source = malfak` (never lose data).
 */

/** The People list filters (`CommentersQuery` without sort) for one account: "select all filtered". */
export const PeopleFilter = CommentersQuery.omit({ sort: true }).extend({ accountId: Id });
export type PeopleFilterT = z.output<typeof PeopleFilter>;

/** Who the action applies to: explicit contact ids, or everyone matching the People filters (minus `exclude`). */
export const PeopleSelection = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("ids"), contactIds: z.array(Id).min(1).max(5000) }),
  z.object({ mode: z.literal("filter"), filter: PeopleFilter, exclude: z.array(Id).max(5000).default([]) }),
]);
export type PeopleSelectionT = z.output<typeof PeopleSelection>;

/** Most people one action may cover, and most comments one comment job may touch (narrow the selection beyond). */
export const PEOPLE_MAX_CONTACTS = 5000;
export const PEOPLE_MAX_COMMENTS = 10_000;

export const PeopleAction = z.discriminatedUnion("type", [
  /** Built-in flags and custom tags on the people (contacts). */
  z.object({ type: z.literal("flag"), add: z.array(FlagKey).max(20).default([]), remove: z.array(FlagKey).max(20).default([]) }),
  /** harmful / troll / vip, or null = clear the mark. */
  z.object({ type: z.literal("mark"), mark: CommenterMark.nullable() }),
  /**
   * Block in Malfak. `accountId` null = every account of the org (default); otherwise only that account.
   * `hideExisting` (default true) hides their visible comments as part of the job (`hidden_reason = blocked_contact`).
   * Blocking someone already blocked updates reason / scope (the original `at` / `by` stay).
   */
  z.object({
    type: z.literal("block"),
    reason: z.string().trim().max(500).optional(),
    accountId: Id.nullable().default(null),
    hideExisting: z.boolean().default(true),
  }),
  /** Unblock: future comments and DMs are treated normally again; `unhide` also unhides the comments the block hid. */
  z.object({ type: z.literal("unblock"), unhide: z.boolean().default(false) }),
  z.object({ type: z.literal("hide_comments") }),
  z.object({ type: z.literal("unhide_comments") }),
  z.object({ type: z.literal("archive_comments") }),
  z.object({ type: z.literal("unarchive_comments") }),
]);
export type PeopleActionT = z.output<typeof PeopleAction>;
export type PeopleActionType = PeopleActionT["type"];

export const PeopleActionRequest = z.object({
  selection: PeopleSelection,
  action: PeopleAction,
  /** Limit comment actions (hide / unhide / archive / block's hide) to one account's comments. */
  accountId: Id.optional(),
});

/** Typed confirmation for "Delete all their comments": the user must type exactly this (in every locale). */
export const PEOPLE_DELETE_CONFIRM = "DELETE";
export const PeopleDeleteRequest = z.object({
  selection: PeopleSelection,
  accountId: Id.optional(),
  confirm: z.string().max(40),
});

export const PeopleExportRequest = z.object({ selection: PeopleSelection });

/** What an action would touch (counts for the confirmation dialogs). */
export const PeopleImpactRequest = z.object({ selection: PeopleSelection, accountId: Id.optional() });
export const PeopleImpact = z.object({
  contacts: z.number().int(),
  blocked: z.number().int(), // of those, already blocked (any scope)
  comments: z.object({
    total: z.number().int(), // not deleted
    visible: z.number().int(),
    hidden: z.number().int(),
    hiddenByBlock: z.number().int(), // hidden_reason = blocked_contact
    deleted: z.number().int(),
  }),
});
export type PeopleImpactT = z.infer<typeof PeopleImpact>;

/** 202: the job (already finished for DB-only actions) + how many people / comments it covers. */
export const PeopleActionResult = z.object({
  job: Job,
  contacts: z.number().int(),
  comments: z.number().int(),
});
export type PeopleActionResultT = z.infer<typeof PeopleActionResult>;

/** Job kind per action (`people.<type>`; delete-all = `people.delete_comments`, export = `people.export`). */
export const peopleJobKind = (type: PeopleActionType | "delete_comments" | "export") => `people.${type}` as const;

// ---------------------------------------------------------------- routes (spread into `routes`)
const route = <P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny>(d: RouteDef<P, Q, B, R>) => d;
const OrgP = z.object({ orgId: z.string().uuid() });

export const peopleRoutes = {
  peopleAction: route({ method: "POST", path: "/v1/orgs/:orgId/people/actions", params: OrgP, body: PeopleActionRequest, response: PeopleActionResult, status: 202, summary: "Bulk moderation over people: flag, mark, block / unblock in Malfak, hide / unhide / archive all their comments (one job, audited)" }),
  peopleDeleteComments: route({ method: "POST", path: "/v1/orgs/:orgId/people/delete-comments", params: OrgP, body: PeopleDeleteRequest, response: PeopleActionResult, status: 202, summary: "Delete every comment of the selected people on the account's media (admin/owner, typed confirmation, job; rows kept with deletion_source=malfak)" }),
  peopleImpact: route({ method: "POST", path: "/v1/orgs/:orgId/people/impact", params: OrgP, body: PeopleImpactRequest, response: PeopleImpact, summary: "Counts an action would touch (people, comments by state), for confirmations" }),
  peopleExport: route({ method: "POST", path: "/v1/orgs/:orgId/people/export", params: OrgP, body: PeopleExportRequest, response: z.string(), summary: "CSV of the selected people (username, name, mark, flags, block, counts); recorded as a job" }),
} as const;
